import React, { useMemo } from 'react';
import { SplitClient, SplitTreatments } from '@splitsoftware/splitio-react';
import { Icon, Div } from '@pinpoint/design-kit';
import { Ubac } from 'src/containers';
import { Button } from 'src/components';
import CONSTANTS from 'src/store/constants';

/* eslint-disable react/prop-types */

function CreateModelFeatureToggleOn({
  client,
  isCreateModelValid,
  isActuarialModelValid,
  showCreateActuarialModal,
  showCreateClassifierModal,
  isAdmin,
  isSuperAdmin,
  meetsValidationMatchedThreshold,
  isDeprecatedPlatform,
}) {
  const disabled =
    (!isCreateModelValid && !isActuarialModelValid) ||
    !isAdmin ||
    (isActuarialModelValid && !meetsValidationMatchedThreshold && !isSuperAdmin) ||
    isDeprecatedPlatform;
  const tooltip = useMemo(() => {
    if (disabled && !isCreateModelValid && !isActuarialModelValid) {
      return 'Select ACTUARIAL, or HIT + MISS (+ VALIDATION)';
    }
    if (disabled && isActuarialModelValid && !meetsValidationMatchedThreshold && !isSuperAdmin) {
      return 'This model does not contain sufficient blind-validation records. Please request approval to train this model using an alternative validation method.';
    }
    if (disabled && isDeprecatedPlatform) {
      return 'The selected segment(s) uses a deprecated platform. Select a segment that is not deprecated.';
    }
    return 'Create a new predictive model';
  }, [
    disabled,
    isActuarialModelValid,
    isCreateModelValid,
    isDeprecatedPlatform,
    isSuperAdmin,
    meetsValidationMatchedThreshold,
  ]);

  return client && client.canModel ? (
    <Div data-test="new-model-btn-div">
      <Button
        buttonStyle="primary"
        disabled={disabled}
        onClick={() => {
          if (isActuarialModelValid) showCreateActuarialModal(true);
          else if (isCreateModelValid) showCreateClassifierModal(true);
        }}
        data-tip={tooltip}
      >
        <Icon name="AddBoxed" viewBox="0 0 14 14" color="white" m={{ r: '0.5rem' }} size=".875rem" />
        NEW MODEL
      </Button>
    </Div>
  ) : (
    <Div data-test="new-model-btn-div">
      <Button
        buttonStyle="primary"
        disabled
        onClick={() => null}
        data-tip="Select ACTUARIAL, or HIT + MISS (+ VALIDATION)"
      >
        <Icon name="AddBoxed" viewBox="0 0 14 14" color="white" m={{ r: '0.5rem' }} size=".875rem" />
        NEW MODEL
      </Button>
    </Div>
  );
}

function CreateModelFeatureToggleOff({
  client,
  isCreateModelValid,
  isActuarialModelValid,
  showCreateModel,
  isAdmin,
  isSuperAdmin,
  meetsValidationMatchedThreshold,
  isDeprecatedPlatform,
}) {
  const disabled =
    (!isCreateModelValid && !isActuarialModelValid) ||
    !isAdmin ||
    (isActuarialModelValid && !meetsValidationMatchedThreshold && !isSuperAdmin) ||
    isDeprecatedPlatform;
  const tooltip = useMemo(() => {
    if (disabled && !isCreateModelValid && !isActuarialModelValid) {
      return 'Select ACTUARIAL, or HIT + MISS (+ VALIDATION)';
    }
    if (disabled && !meetsValidationMatchedThreshold && !isSuperAdmin) {
      return 'This model does not contain sufficient blind-validation records. Please request approval to train this model using an alternative validation method.';
    }
    if (disabled && isDeprecatedPlatform) {
      return 'The selected segment(s) uses a deprecated platform. Select a segment that is not deprecated.';
    }
    return 'Create a new predictive model';
  }, [
    disabled,
    isActuarialModelValid,
    isCreateModelValid,
    isDeprecatedPlatform,
    isSuperAdmin,
    meetsValidationMatchedThreshold,
  ]);

  return client && client.canModel ? (
    <Ubac users={CONSTANTS.ENG_TEAM}>
      <Button
        buttonStyle="primary"
        disabled={disabled}
        data-tip={tooltip}
        onClick={() => {
          showCreateModel(true);
        }}
      >
        <Icon name="AddBoxed" viewBox="0 0 14 14" color="white" m={{ r: '0.5rem' }} size=".875rem" />
        NEW MODEL
      </Button>
    </Ubac>
  ) : null;
}

export function CreateModelFeatureToggle({ tenant, ...rest }) {
  return (
    <SplitClient splitKey={tenant}>
      <SplitTreatments names={[CONSTANTS.SPLIT_NAMES.CAN_CREATE_THINKALIKE_MODELS]}>
        {({ treatments, isReady }) => {
          if (!isReady) {
            return null;
          }
          if (treatments[CONSTANTS.SPLIT_NAMES.CAN_CREATE_THINKALIKE_MODELS].treatment === 'on') {
            return <CreateModelFeatureToggleOn {...rest} />;
          }
          return <CreateModelFeatureToggleOff {...rest} />;
        }}
      </SplitTreatments>
    </SplitClient>
  );
}

function PredictModelFeatureToggleOn({ client, isPredictModelValid, showPredictModel, isAdmin, isDeprecatedPlatform }) {
  const disabled = !isPredictModelValid || !isAdmin || isDeprecatedPlatform;
  const tooltip = useMemo(() => {
    if (disabled && (!isPredictModelValid || !isAdmin)) {
      return 'Select TARGET';
    }
    if (disabled && isDeprecatedPlatform) {
      return 'The selected segment(s) uses a deprecated platform. Select a segment that is not deprecated.';
    }
    return 'Write out predictions to dataset';
  }, [disabled, isPredictModelValid, isAdmin, isDeprecatedPlatform]);

  return client && client.canScore ? (
    <Div data-test="predict-btn-div">
      <Button
        buttonStyle="primary"
        disabled={disabled}
        data-tip={tooltip}
        onClick={() => {
          showPredictModel(true);
        }}
      >
        <Icon name="ThinkalikeModel" color="white" m={{ r: '0.5rem' }} size="1rem" />
        PREDICT
      </Button>
    </Div>
  ) : null;
}

function PredictModelFeatureToggleOff({ client, isPredictModelValid, showPredictModel, isAdmin }) {
  const disabled = !isPredictModelValid || !isAdmin;
  return client && client.canScore ? (
    <Ubac users={CONSTANTS.ENG_TEAM}>
      <Div data-test="predict-btn-div">
        <Button
          buttonStyle="primary"
          disabled={disabled}
          data-tip={disabled ? 'Select TARGET' : 'Write out predictions to dataset'}
          onClick={() => {
            showPredictModel(true);
          }}
        >
          <Icon name="ThinkalikeModel" color="white" m={{ r: '0.5rem' }} size="1rem" />
          PREDICT
        </Button>
      </Div>
    </Ubac>
  ) : null;
}

export function PredictModelFeatureToggle({ tenant, ...rest }) {
  return (
    <SplitClient splitKey={tenant}>
      <SplitTreatments names={[CONSTANTS.SPLIT_NAMES.CAN_CREATE_THINKALIKE_MODELS]}>
        {({ treatments, isReady }) => {
          if (!isReady) {
            return null;
          }
          if (treatments[CONSTANTS.SPLIT_NAMES.CAN_CREATE_THINKALIKE_MODELS].treatment === 'on') {
            return <PredictModelFeatureToggleOn {...rest} />;
          }
          return <PredictModelFeatureToggleOff {...rest} />;
        }}
      </SplitTreatments>
    </SplitClient>
  );
}
